/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  GridApi,
  GridReadyEvent, PostSortRowsParams,
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {FCORG_BLACKLIST} from '../../../common/constants';
import {IndustryScenario} from '../../../models/industry-scenario-dto.model';
import {IndustryScenarioService} from '../../../services/industry-scenario.service';
import {AutocompleteFilterComponent} from '../../filters/autocomplete-filter/autocomplete-filter.component';
import {StateService} from '../../state/state.service';

@Component({
    selector: 'app-industry-scenario-select',
    templateUrl: './industry-scenario-select.component.html',
    styleUrl: './industry-scenario-select.component.scss',
    standalone: false
})
export class IndustryScenarioSelectComponent implements OnInit {
  @Input() titleText: string;
  @Input() validFcorgs: string[] = [];
  @Input() isMultiSelect: boolean = false;
  @Output() confirmEvent = new EventEmitter<any>();


  gridApi: GridApi;
  displayedColumns: string[];
  selectedScenarios: any[] = [];
  showSpinner: boolean;
  pageSize: number;

  scenarios: any[] = [];
  displayedData: IndustryScenario[] = [];
  displayHiddenScenarios = false;

  industryScenarioColumnHeaders: ColDef[] = [
    {field: 'id', headerName: 'Industry Scenario ID', filter: AutocompleteFilterComponent, lockPosition: true},
    {
      field: 'industryScenarioName',
      headerName: 'Industry Scenario Name',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    },
    {field: 'forecastOrganization', headerName: 'FCORG', filter: AutocompleteFilterComponent, lockPosition: true},
    {
      field: 'industryScenarioAssumption',
      headerName: 'Industry Scenario Assumption',
      filter: AutocompleteFilterComponent,
      lockPosition: true
    }
  ];

  rowSelection: RowSelectionOptions = {
    mode: this.isMultiSelect ? 'multiRow' : 'singleRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
    headerCheckbox: false,
  };

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };

  postSortRows: (params: PostSortRowsParams) => void = (
    params: PostSortRowsParams<IndustryScenario>,
  ) => {
    // Only post sort rows if the user has a primary FCORG preference
    if (this.stateService.userPreferences?.some(pref => pref.preferenceKey === 'Primary FCORG')) {
      const rowNodes = params.nodes;
      // here we put the Primary FCORG rows on top while preserving the sort order if the user has a primary FCORG preference
      let nextInsertPos = 0;
      for (let i = 0; i < rowNodes.length; i++) {
        const fcorg = rowNodes[i].data ? rowNodes[i].data!.forecastOrganization : undefined;
        if (fcorg === this.stateService.userPreferences.find(pref => pref.preferenceKey === 'Primary FCORG')?.preferenceValue) {
          rowNodes.splice(nextInsertPos, 0, rowNodes.splice(i, 1)[0]);
          nextInsertPos++;
        }
      }
    }
  };

  constructor(readonly industryScenarioService: IndustryScenarioService,
              public stateService: StateService,
              public snackBar: FabMessageService) {
  }

  ngOnInit(): void {
    this.pageSize = 100;
    this.getIndustryScenarios();
  }

  /**
   * Gets the grid API and data when the grid is ready
   * @param params the parameters generated by the grid event
   */
  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridApi.setGridOption("rowSelection", {
      mode: this.isMultiSelect ? 'multiRow' : 'singleRow',
      checkboxes: false,
      enableClickSelection: true,
      enableSelectionWithoutKeys: true,
      headerCheckbox: false,
    });
  }

  /**
   * Fetches the vehicle scenarios and filters them by the input parameters
   */
  public getIndustryScenarios(): void {
    this.showSpinner = true;
    this.industryScenarioService.getIndustryScenarioByGmid(this.stateService.getUserGmid()).subscribe(
      (scenarios) => {
        this.scenarios = scenarios.filter((row: IndustryScenario) => !FCORG_BLACKLIST.includes(row.forecastOrganization));
        if (this.validFcorgs?.length > 0) {
          this.scenarios = this.scenarios.filter(row => this.validFcorgs.includes(row.forecastOrganization));
        }
        this.displayedData = this.scenarios.filter(row => row.isHidden === false);
      },
      (error) => {
        this.snackBar.displayError(error);
        this.showSpinner = false;
      },
      () => {
        this.showSpinner = false;
      }
    );
  }


  /**
   * When the hidden checkbox or fcorg filter change, alter the data displayed in the table
   */
  setTableData(): void {
    this.displayedData = this.scenarios;
    if (!this.displayHiddenScenarios) {
      this.displayedData = this.scenarios.filter(row => row.isHidden === false);
    }
    this.selectedScenarios = [];
    this.emitEvent();
  }

  /**
   * Sets the scenario as selected
   * @param event the event when a scenario is selected
   */
  onSelect(event: any) {
    const row: IndustryScenario = event.data;
    if (!event.node.selected) {
      this.selectedScenarios = this.selectedScenarios.filter(element => element != row);
    } else {
      if (this.isMultiSelect) {
        this.selectedScenarios.push(row);
      } else {
        this.selectedScenarios = [row];
      }
    }
    this.emitEvent();
  }


  /**
   * sends data when event emitted
   */
  emitEvent() {
    this.confirmEvent.emit({
      selectedScenarios: this.selectedScenarios,
    });
  }
}
